/**
 * Host analytics adapter for the federated Activity Editor (MR-9305). The editor emits
 * finished Action-Artifact-V2 events (H5); this host only adds what a remote cannot know
 * and forwards to `window.wb_track`. It never renames, maps or drops a spec field.
 */

type Details = Record<string, unknown>;

const ARTIFACT_EVENT = 'ARTIFACT';

// Dual-fire natives arrive without artifact context. Only the three the spec names
// outright; the rest of its table is still "(confirm)". RELATIONSHIP has no emitter in
// the current remote, and is kept only because the spec's dual-fire table lists it.
const NATIVE_EDITOR_EVENTS = new Set(['AI UPDATE', 'COMMENT', 'RELATIONSHIP']);

/** Builds the details for `wb_track`. Host-owned surface fields win; editor fields are only filled in. */
export const enrichActivityEditorEvent = (
  name: string,
  details: Details | undefined,
  activityId: string,
): Details => {
  // `location` is the host's to own, never the remote's; an older served remote may still stamp it.
  const { location: _location, ...fields }: Details = { ...details };
  const enriched: Details = {
    ...fields,
    hostSurface: 'WORKBOARD',
    appModality: 'Webapp',
  };

  if (name === ARTIFACT_EVENT) {
    // Fill only: OPEN_SUBTASK and OPEN_RELATIONSHIP_OBJECT name another object as resourceId.
    if (fields.artifactType === undefined) enriched.artifactType = 'ACTION';
    if (fields.resourceId === undefined) enriched.resourceId = activityId;
    return enriched;
  }

  if (NATIVE_EDITOR_EVENTS.has(name)) {
    if (fields.action === undefined) enriched.action = name;
    if (fields.artifactType === undefined) enriched.artifactType = 'ACTION';
    const native = fields.activityId;
    if (
      fields.resourceId === undefined &&
      (typeof native === 'string' || typeof native === 'number')
    ) {
      enriched.resourceId = String(native);
    }
  }

  return enriched;
};

export const activityEditorAnalytics = (activityId: string) => ({
  trackEvent: (name: string, details?: Details) => {
    try {
      // Read per call: wb_events.js may load after this module, and wb_track mutates
      // the object it is handed, so each call gets its own.
      window.wb_track?.(
        name,
        enrichActivityEditorEvent(name, details, activityId),
      );
    } catch (err) {
      console.warn('Activity Editor analytics event dropped', name, err);
    }
  },
});
